// Shared social-share URL builders and CMS visibility gating, used by both the
// campaign/life cards and the "Share the link" modal so the two entry points
// stay in sync.
import { getStoredUtms } from "./utmStorage";

// Every shared link carries tracking, or the friend who opens it has no UTMs and
// their donation is counted under "Others". Like our other own links (wa, email,
// p_donateonce) a share is marked by cutm_platform=shared. The sharer's own UTMs
// ride along unchanged, so source, campaign, editor (utm_content) and ad keep their
// credit; a sharer who has none gets utm_source=share and the button as the medium.
const SHARE_MEDIUM = {
  whatsapp: "whatsapp",
  facebook: "facebook",
  twitter: "x",
  linkedin: "linkedin",
  gmail: "email",
  copy: "copy",
};

const CARRIED_UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_id",
  "utm_content",
  "utm_term",
  "utm_matchtype",
  "ad_id",
  "adset_id",
  "adgroup_id",
];

/** `url` with the share tracking for `platformKey` (whatsapp, facebook, …, copy). */
export const withShareTracking = (url, platformKey) => {
  const medium = SHARE_MEDIUM[platformKey];
  if (!url || !medium) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    const stored = getStoredUtms();
    const carried = CARRIED_UTM_KEYS.filter((key) => stored[key]);
    if (carried.length) {
      carried.forEach((key) => parsed.searchParams.set(key, stored[key]));
    } else {
      parsed.searchParams.set("utm_source", "share");
      parsed.searchParams.set("utm_medium", medium);
    }
    parsed.searchParams.set("cutm_platform", "shared");
    return parsed.toString();
  } catch (_) {
    return url;
  }
};

// Tag the site's own links inside a share message; other links are left as they are.
const withShareTrackingInText = (text, platformKey) => {
  if (!text || typeof text !== "string") return text;
  return text.replace(/https?:\/\/[^\s,)'"]+/g, (match) => {
    try {
      if (new URL(match).origin !== window.location.origin) return match;
    } catch (_) {
      return match;
    }
    return withShareTracking(match, platformKey);
  });
};

const UNTRACKED_SHARE_URL_BUILDERS = {
  whatsapp: (url, message) => `https://wa.me/?text=${encodeURIComponent(message || url || "")}`,
  facebook: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url || "")}`,
  twitter: (url, message) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(message || url || "")}`,
  linkedin: (url) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url || "")}`,
  gmail: (url, message) => `mailto:?body=${encodeURIComponent(message || url || "")}`,
};

export const SHARE_URL_BUILDERS = Object.fromEntries(
  Object.entries(UNTRACKED_SHARE_URL_BUILDERS).map(([key, build]) => [
    key,
    (url, message) =>
      build(withShareTracking(url, key), withShareTrackingInText(message, key)),
  ])
);

export const buildSocialShareUrl = (platformKey, url, message) => {
  const builder = SHARE_URL_BUILDERS[platformKey];
  return builder ? builder(url, message) : "";
};

// `status` is the admin's master "Enable Social Media Share" toggle, not a
// "config exists" flag — admin sends status: 0 (not null) when it's switched
// off. No row at all (legacy campaigns, or status missing) hides all
// platforms; status 0 also hides all; status 1 defers to the individual flags.
export const getAvailableSocialPlatforms = (socialMediaLinks) => {
  if (!socialMediaLinks) {
    return { facebook: false, linkedin: false, twitter: false };
  }
  if (socialMediaLinks.status !== 1) {
    return { facebook: false, linkedin: false, twitter: false };
  }
  return {
    facebook: socialMediaLinks.facebook === true,
    linkedin: socialMediaLinks.linkedin === true,
    twitter: socialMediaLinks.twitter === true,
  };
};
